import moment from 'moment'
import { fillArray } from 'lib/array/arrayUtils'
import { dateIsAfter, dateIsBefore, endOfMonth, isSameDay, isSameMonth } from 'lib/datetime/dateUtils'

export type CellRanges = {
  start: number
  end: number
}

export function calcGridPositionsForVerticalMonth(firstDateInMonth: Date, startDate?: Date, endDate?: Date): {
  startRow?: number
  endRow?: number
  range?: CellRanges
} {
  const monthEnd = endOfMonth(firstDateInMonth)
  const monthStartOffset = firstDateInMonth.getDate()

  let startRow: number | undefined, endRow: number | undefined, range: CellRanges | undefined
  if (startDate) {
    if (isSameMonth(startDate, firstDateInMonth)) {
      // starts in this month
      startRow = startDate.getDate() - monthStartOffset + 1
    }
  }

  if (endDate) {
    if (isSameMonth(endDate, firstDateInMonth)) {
      // ends in this month
      endRow = endDate.getDate() - monthStartOffset + 2
    }
  }

  if (startDate && endDate) {
    // calcuate our range positions
    if (dateIsBefore(startDate, firstDateInMonth) && dateIsAfter(endDate, monthEnd)) {
      // spans the whole month
      range = {
        start: 1,
        end: monthEnd.getDate() - monthStartOffset,
      }
    } else {
      let start, end
      if (isSameMonth(startDate, firstDateInMonth)) {
        // starts in this month, find the row it starts at
        start = startDate.getDate() - monthStartOffset + 2
      }

      if (dateIsBefore(startDate, firstDateInMonth) && !isSameDay(endDate, firstDateInMonth)) {
        // starts in a previous month, so our range starts on the first day (unless that's also the end)
        start = 1
      }

      if (dateIsAfter(endDate, monthEnd)) {
        // ends in a later away month, so our range is all the way to the end
        end = monthEnd.getDate() - monthStartOffset + 2
      }

      if (isSameMonth(endDate, firstDateInMonth)) {
        // ends in this month, find the row it ends at
        end = endDate.getDate() - monthStartOffset + 1
      }

      if (start && end) {
        range = { start, end }
      }
    }
  }

  return {
    startRow,
    endRow,
    range,
  }
}

/**
 * Create Date obj in user local timezone
 *
 * @param {string} dateString - 2022-01-30
 * @return {Date}
 */
export function getLocalDate(dateString: String) {
  return new Date(`${dateString}T00:00`)
}

/**
 * Get coordinates (month, day)
 */
export function getCalendarDateCoordinates(dateString: string, calendar: App.Calendar) {
  const date = moment.utc(dateString)
  const monthKey = `${date.month() + 1}-${date.year()}`

  const monthIdx = calendar.months.findIndex(month => month.key === monthKey)

  const month = calendar.months[monthIdx]
  const dayIdx = month.days.findIndex(day => day.checkIn === dateString)

  return { monthIdx, dayIdx }
}

export const BEDBANK_CALENDAR_FUTURE_DAYS = 500

export type CalendarWeekStart = 0 | 1 | 2 | 3 | 4 | 5 | 6

/** Uses the platform's CLDR locale data; falls back to Monday when unavailable. */
export function getCalendarWeekStart(locale?: string): CalendarWeekStart {
  try {
    const localeInfo = new Intl.Locale(locale ?? 'en-AU') as Intl.Locale & { weekInfo?: { firstDay: number } }
    const firstDay = (localeInfo.getWeekInfo?.().firstDay ?? localeInfo.weekInfo?.firstDay ?? 1) % 7
    return firstDay as CalendarWeekStart
  } catch {
    return 1
  }
}

export function getCalendarWeekdayIndex(date: Date, weekStart: CalendarWeekStart = 1) {
  return (date.getDay() - weekStart + 7) % 7
}

export function orderCalendarWeekdays<T>(weekdays: ReadonlyArray<T>, weekStart: CalendarWeekStart): Array<T> {
  return [...weekdays.slice(weekStart), ...weekdays.slice(0, weekStart)]
}

/**
 * Retrieves the empty cells that should be shown at the start of a calendar month.
 * our month may not start on the first day of the month, because we can't check in
 * on those days (they may be in the past). So we fill in the earliest weeks cells
 */
export function getCalendarMonthEmptyCells(firstDayOfMonth: App.CalendarDay | App.BedbankCalendarDay, weekStart: CalendarWeekStart = 1) {
  if (firstDayOfMonth.day !== 1) {
    const checkIn = getLocalDate(firstDayOfMonth.checkIn)
    const firstWeekdayIndex = getCalendarWeekdayIndex(checkIn, weekStart)
    return inMonthLeadingCells(checkIn, firstWeekdayIndex)
  }
  return []
}

/**
 * Retrieves the empty cells that should be shown at the start of a calendar month.
 * our month may not start on the first day of the month, because we can't check in
 * on those days (they may be in the past). So we fill in the earliest weeks cells
 * This is different from getCalendarMonthEmptyCells because it works from a date
 * string rather than a calendar day object.
 */
export function getSidebarCalendarMonthEmptyCells(firstDateOfMonth: string, weekStart: CalendarWeekStart = 1) {
  const checkIn = getLocalDate(firstDateOfMonth)
  const firstWeekdayIndex = getCalendarWeekdayIndex(checkIn, weekStart)
  if (firstWeekdayIndex !== 0) {
    return inMonthLeadingCells(checkIn, firstWeekdayIndex)
  }
  return []
}

/** Leading cells are only in-month days. A 1st that is not the week start stays blank. */
function inMonthLeadingCells(checkIn: Date, firstWeekdayIndex: number) {
  return fillArray(firstWeekdayIndex, (index) => ({
    day: checkIn.getDate() - firstWeekdayIndex + index,
    column: index + 1,
  })).filter(cell => cell.day >= 1)
}
